/**
 * Writing to a student's inbox. Management may write to anyone on the roster;
 * an instructor only to the students assigned to them, which is what the
 * recipient list returns.
 */
import { useState } from 'react';
import { api, useQuery } from '../api.js';
import { Avatar } from '../avatar.js';
import { SearchField, matchesSearch } from '../controls.js';
import { Drawer, Spinner, useToast } from '../ui.js';

interface Recipient { id: number; name: string; userId: number; avatarUpdatedAt: number | null }

export function Compose({ to, onClose, onSent }: {
  /** Preselected recipient, when writing from a student's record. */
  to?: { id: number; name: string } | null;
  onClose: () => void;
  onSent?: () => void;
}): JSX.Element {
  const { data } = useQuery<{ recipients: Recipient[] }>('/inbox/recipients');
  const toast = useToast();

  const [picked, setPicked] = useState<number[]>(to ? [to.id] : []);
  const [query, setQuery] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const recipients = (data?.recipients ?? []).filter((r) => matchesSearch(query, r.name));
  const toggle = (id: number) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const send = async () => {
    setBusy(true);
    try {
      const { sent } = await api.post<{ sent: number }>('/inbox/messages', {
        studentIds: picked, title: title.trim(), body: body.trim(),
      });
      toast.ok(`Sent to ${sent} ${sent === 1 ? 'student' : 'students'}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error('Could not send that', err instanceof Error ? err.message : undefined);
      setBusy(false);
    }
  };

  const missing = [
    picked.length === 0 ? 'a recipient' : '',
    !title.trim() ? 'a subject' : '',
    !body.trim() ? 'a message' : '',
  ].filter(Boolean);

  return (
    <Drawer
      narrow
      title="New message"
      subtitle={to ? `To ${to.name}` : 'To students in your school'}
      onClose={onClose}
      footer={
        <>
          <span className="small muted" style={{ flex: 1, minWidth: 0 }}>
            {missing.length > 0 && `Still needs ${missing.join(', ')}.`}
          </span>
          <button className="btn btn--primary" disabled={busy || missing.length > 0} onClick={send}>
            {busy ? 'Sending…' : `Send${picked.length > 1 ? ` to ${picked.length}` : ''}`}
          </button>
        </>
      }
    >
      <div className="card__body stack" style={{ gap: 14 }}>
        {!data && <Spinner />}

        {data && !to && (
          <div className="field">
            <span>To</span>
            <SearchField value={query} onChange={setQuery} label="Search students" placeholder="Search students" />
            <div className="compose__list">
              {recipients.length === 0 && <div className="empty" style={{ padding: 16 }}>Nobody matches.</div>}
              {recipients.map((r) => (
                <label className="compose__row" key={r.id}>
                  <input
                    type="checkbox"
                    checked={picked.includes(r.id)}
                    onChange={() => toggle(r.id)}
                  />
                  <Avatar userId={r.userId} updatedAt={r.avatarUpdatedAt} initials={initials(r.name)} size={24} />
                  <span>{r.name}</span>
                </label>
              ))}
            </div>
            <span className="small muted" style={{ display: 'block', marginTop: 6 }}>
              {picked.length} selected.
            </span>
          </div>
        )}

        <label className="field">
          <span>Subject</span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Weather for Saturday" autoFocus={!!to} />
        </label>

        <label className="field">
          <span>Message</span>
          <textarea className="textarea" style={{ minHeight: 140 }} value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What you want them to know before the next lesson." />
        </label>

        <p className="small muted" style={{ margin: 0, textWrap: 'pretty' }}>
          It arrives in the student's inbox with your name on it. This is not email — they see it
          when they next open the portal.
        </p>
      </div>
    </Drawer>
  );
}

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}
